import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

function extractListPayload(res) {
  if (!res || typeof res !== 'object') {
    return { items: [], total: 0, lastSyncedAt: null, neverSynced: true };
  }
  const items = Array.isArray(res.items)
    ? res.items
    : (Array.isArray(res.content) ? res.content : (Array.isArray(res.data) ? res.data : []));
  const total = Number.isFinite(Number(res.totalElements))
    ? Number(res.totalElements)
    : (Number.isFinite(Number(res.total)) ? Number(res.total) : items.length);
  const lastSyncedAt = res.lastSyncedAt || res.syncedAt || null;
  const neverSynced = Boolean(res.neverSynced) || (!lastSyncedAt && items.length === 0);
  return { items, total, lastSyncedAt, neverSynced };
}

const MODULE_TABLE_META = {
  marketing: {
    keywordLabel: '关键词',
    keywordPlaceholder: '活动名称 / 活动 ID',
    columns: [
      { key: 'name', get label() { return fixedUi("活动名称"); }, pick: (row) => row.name || row.activityName || row.title || '—' },
      { key: 'type', label: '活动类型', pick: (row) => row.type || row.activityType || '—' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || row.activityStatus || '—' },
      { key: 'start', label: '开始时间', pick: (row) => row.startTime || row.startAt || '—' },
      { key: 'end', label: '结束时间', pick: (row) => row.endTime || row.endAt || '—' },
    ],
  },
  fulfillment: {
    keywordLabel: '关键词',
    keywordPlaceholder: '采购子单号 / 货号 / 商品名',
    columns: [
      { key: 'sn', label: '采购子单号', pick: (row) => row.subPurchaseOrderSn || row.orderSn || row.sn || '—' },
      { key: 'name', get label() { return fixedUi("商品"); }, pick: (row) => row.productName || row.name || '—' },
      { key: 'skc', label: 'SKC', pick: (row) => row.productSkcId || '—' },
      { key: 'sku', get label() { return fixedUi("货号"); }, pick: (row) => row.skcExtCode || row.skuCode || '—' },
      { key: 'warehouse', label: '收货仓', pick: (row) => row.subWarehouseName || '—' },
      { key: 'status', label: '状态/紧急度', pick: (row) => row.status || '—' },
      { key: 'qty', get label() { return fixedUi("数量"); }, pick: (row) => row.quantity ?? row.qty ?? row.purchaseQuantity ?? '—' },
      { key: 'eta', label: '最晚到货', pick: (row) => row.expectLatestArrival || '—' },
    ],
  },
  quality: {
    keywordLabel: '关键词',
    keywordPlaceholder: '质检单 / 采购单 / SKC',
    columns: [
      { key: 'bill', label: '质检单', pick: (row) => row.qcBillId || row.billId || row.id || '—' },
      { key: 'purchase', label: '采购单', pick: (row) => row.purchaseNo || row.subPurchaseOrderSn || '—' },
      { key: 'sku', label: 'SKU', pick: (row) => row.skuName || row.productSkuId || '—' },
      { key: 'skc', label: 'SKC', pick: (row) => row.productSkcId || '—' },
      { key: 'status', label: '结果', pick: (row) => row.status || row.skuQcResult || '—' },
    ],
  },
  returnOrders: {
    keywordLabel: '关键词',
    keywordPlaceholder: '退供单号 / 订单号',
    columns: [
      { key: 'sn', label: '退供单号', pick: (row) => row.returnOrderNo || row.packageSn || row.sn || '—' },
      { key: 'order', label: '关联订单', pick: (row) => row.orderNo || row.parentOrderSn || '—' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || row.returnStatus || '—' },
      { key: 'skc', label: 'SKC', pick: (row) => row.productSkcId || row.skcName || '—' },
    ],
  },
  print: {
    keywordLabel: '关键词',
    keywordPlaceholder: '发货单号 / 运单号',
    columns: [
      { key: 'task', label: '发货单号', pick: (row) => row.deliveryOrderSn || row.taskNo || row.id || '—' },
      { key: 'express', get label() { return fixedUi("运单号"); }, pick: (row) => row.expressDeliverySn || '—' },
      { key: 'name', get label() { return fixedUi("商品"); }, pick: (row) => row.productName || row.name || '—' },
      { key: 'type', get label() { return fixedUi("类型"); }, pick: (row) => row.printType || row.type || '箱唛' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || '—' },
    ],
  },
  aftersale: {
    keywordLabel: '关键词',
    keywordPlaceholder: '寄样单号 / SKC',
    columns: [
      { key: 'sn', label: '寄样单号', pick: (row) => row.sampleSn || row.sn || '—' },
      { key: 'name', get label() { return fixedUi("商品"); }, pick: (row) => row.productName || row.name || '—' },
      { key: 'skc', label: 'SKC', pick: (row) => row.skcName || row.productSkcId || '—' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || '—' },
    ],
  },
  packing: {
    keywordLabel: '关键词',
    keywordPlaceholder: '包装任务 / SKU',
    columns: [
      { key: 'task', label: '任务/ID', pick: (row) => row.taskNo || row.id || '—' },
      { key: 'name', get label() { return fixedUi("商品"); }, pick: (row) => row.productName || row.name || '—' },
      { key: 'sku', label: 'SKU', pick: (row) => row.skuCode || row.productSkuId || '—' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || row.suitStatus || '—' },
    ],
  },
  ads: {
    keywordLabel: '关键词',
    keywordPlaceholder: '广告计划 / 商品',
    columns: [
      { key: 'plan', label: '计划名称', pick: (row) => row.planName || row.name || '—' },
      { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || row.adStatus || '—' },
      { key: 'budget', label: '预算', pick: (row) => row.budget ?? '—' },
      { key: 'synced', label: '同步时间', pick: (row) => row.syncedAt || '—' },
    ],
  },
  sales: {
    keywordLabel: '关键词',
    keywordPlaceholder: 'SKU / SKC / 商品名',
    columns: [
      { key: 'name', get label() { return fixedUi("商品"); }, pick: (row) => row.productName || row.name || '—' },
      { key: 'skc', label: 'SKC', pick: (row) => row.productSkcId || '—' },
      { key: 'sku', get label() { return fixedUi("货号"); }, pick: (row) => row.skcExtCode || row.sku || row.skuCode || '—' },
      { key: 'today', label: '今日销量', pick: (row) => row.todaySaleVolume ?? row.sales ?? '—' },
      { key: 'd7', label: '近7日', pick: (row) => row.sevenDaysSaleVolume ?? row.salesVolume ?? '—' },
      { key: 'd30', label: '近30日', pick: (row) => row.thirtyDaysSaleVolume ?? '—' },
      { key: 'inv', label: '剩余库存', pick: (row) => row.remanentInventoryNum ?? '—' },
    ],
  },
};

const DEFAULT_TABLE_META = {
  keywordLabel: '关键词',
  keywordPlaceholder: '名称 / 编号',
  columns: [
    { key: 'name', get label() { return fixedUi("名称"); }, pick: (row) => row.name || row.title || row.id || '—' },
    { key: 'status', get label() { return fixedUi("状态"); }, pick: (row) => row.status || '—' },
    { key: 'updated', get label() { return fixedUi("更新时间"); }, pick: (row) => row.updatedAt || row.lastSyncedAt || row.syncedAt || '—' },
  ],
};

const SYNC_HINTS = {
  fulfillment: '从 TEMU CN 区拉取发货台与备货采购单，更新到 IPE 本地。',
  print: '从 TEMU 拉取发货单/箱唛缓存；可对发货单号请求打印。',
  aftersale: '从 TEMU 拉取寄样单到本地。',
  quality: '从 TEMU 拉取质检单本地缓存。',
  returnOrders: '从 TEMU 拉取退供包裹本地缓存。',
  packing: '从 TEMU 拉取包装合规套装属性。',
  ads: '从 TEMU 拉取全托广告计划。',
  sales: '从 TEMU 拉取 SKC 销量数据。',
  marketing: '从 TEMU 拉取可报名活动列表、详情与场次，更新到 IPE 本地。',
};

/**
 * 全托运维列表页：查询区 + 工具栏 + 表格（对齐价格处理布局）。
 */
export default function TemuPhase2OpsStubPage({
  track = 'full',
  title,
  moduleKey,
  listFnName,
  syncFnName,
  enablePrintAction = false,
}) {
  const tableMeta = MODULE_TABLE_META[moduleKey] || DEFAULT_TABLE_META;
  const syncKind = String(moduleKey || 'custom');
  const syncKinds = useMemo(() => [syncKind], [syncKind]);

  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId || '';
  const { runBlocking, isBusy } = useTemuSyncJobs();
  const [filterDraft, setFilterDraft] = useState({ keyword: '' });
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [neverSynced, setNeverSynced] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const listFn = listFnName ? temuPlatformAPI[listFnName] : null;
  const syncFn = syncFnName ? temuPlatformAPI[syncFnName] : null;

  const lastSyncedLabel = useMemo(() => {
    if (!lastSyncedAt) return '尚未同步';
    try {
      return `最近同步：${formatSiteListSyncedAt(lastSyncedAt)}`;
    } catch {
      return `最近同步：${String(lastSyncedAt)}`;
    }
  }, [lastSyncedAt]);

  const loadList = useCallback(async (sid, kw) => {
    if (!sid || typeof listFn !== 'function') {
      setRows([]);
      setTotal(0);
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await listFn(sid, { track, keyword: kw || undefined, page: 0, size: 50 });
      const payload = extractListPayload(res);
      setRows(payload.items);
      setTotal(payload.total);
      setLastSyncedAt(payload.lastSyncedAt);
      setNeverSynced(payload.neverSynced);
      setSelected(new Set());
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || `加载${title}失败`);
      showToast(e?.message || `加载${title}失败`, 'error');
    } finally {
      setLoading(false);
    }
  }, [listFn, track, title]);

  useEffect(() => {
    loadList(shopId, appliedKeyword);
  }, [shopId, appliedKeyword, loadList]);

  const onQuery = () => {
    setAppliedKeyword(String(filterDraft.keyword || '').trim());
  };

  const onReset = () => {
    setFilterDraft({ keyword: '' });
    setAppliedKeyword('');
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择 TEMU 店铺"), 'warning');
      return;
    }
    if (typeof syncFn !== 'function') {
      showToast(fixedUi("同步接口尚未就绪"), 'info');
      return;
    }
    try {
      await runBlocking({
        kind: syncKind,
        shopId,
        get label() { return fixedUi("同步更新"); },
        syncFn: () => syncFn(shopId, { track }),
        buildSuccess: (res) => ({
          imported: Number(res?.imported ?? res?.total ?? 0) || 0,
          message: `${title}同步完成`,
        }),
      });
      await loadList(shopId, appliedKeyword);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
    }
  }, [shopId, track, title, syncFn, syncKind, loadList, appliedKeyword, runBlocking]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy(syncKind, shopId);

  const rowKey = (row, index) => String(
    row.id
    || row.subPurchaseOrderSn
    || row.deliveryOrderSn
    || row.sampleSn
    || row.taskNo
    || row.productSkcId
    || index,
  );

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(rows.map((r, i) => rowKey(r, i))));
  };

  const resolveSelectedRows = () => (
    rows.filter((row, i) => selected.has(rowKey(row, i)))
  );

  const clearSelection = () => setSelected(new Set());

  const exportRows = (source) => {
    const list = Array.isArray(source) ? source : [];
    if (!list.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    const headers = tableMeta.columns.map((c) => fixedUi(c.label));
    const body = list.map((row) => {
      const o = {};
      tableMeta.columns.forEach((col) => {
        o[fixedUi(col.label)] = col.pick(row);
      });
      return o;
    });
    downloadCsv(`temu-${moduleKey}-${Date.now()}.csv`, headers, body);
    showToast(`已导出 ${list.length} 条`, 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择 TEMU 店铺"), 'warning');
      return;
    }
    if (typeof listFn !== 'function') {
      showToast(fixedUi("列表接口尚未就绪"), 'info');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const kw = appliedKeyword || undefined;
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await listFn(shopId, {
          track,
          keyword: kw,
          page: Math.max(0, pageNo - 1),
          size,
        });
        const payload = extractListPayload(res);
        return { items: payload.items, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  };

  const copySelectedIds = async () => {
    const list = resolveSelectedRows();
    if (!list.length) {
      showToast(fixedUi("请先勾选数据"), 'warning');
      return;
    }
    const ids = list.map((row, i) => rowKey(row, i)).filter(Boolean);
    const unique = [...new Set(ids)];
    const text = unique.join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast(`已复制 ${unique.length} 个编号`, 'success');
    } catch {
      showToast(fixedUi("复制失败"), 'error');
    }
  };

  const printSelected = async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    const sns = [];
    resolveSelectedRows().forEach((row) => {
      const sn = row.deliveryOrderSn || row.taskNo;
      if (sn) sns.push(String(sn));
    });
    if (!sns.length) {
      showToast(fixedUi("请勾选含发货单号的行，或同步打印中心后再试"), 'warning');
      return;
    }
    setPrinting(true);
    try {
      const res = await temuPlatformAPI.printLabels(shopId, {
        kind: 'boxmark',
        deliveryOrderSnList: sns,
        return_data_key: true,
      });
      const url = res?.printUrl || res?.url;
      if (url && typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
        showToast(`已打开打印页（${sns.length} 单）`, 'success');
      } else if (res?.dataKey) {
        showToast(`已获取 dataKey：${res.dataKey}`, 'success');
      } else {
        showToast(res?.msg || '打印接口已调用，请查看返回结果', 'info');
      }
    } catch (e) {
      showToast(e?.message || '打印失败', 'error');
    } finally {
      setPrinting(false);
    }
  };

  const batchOpsMenu = useMemo(() => {
    const menu = [
      {
        key: 'export-selected',
        label: fixedUi('导出选中 CSV'),
        disabled: selected.size === 0,
        onClick: () => exportRows(resolveSelectedRows()),
      },
      {
        key: 'copy-ids',
        label: fixedUi('复制选中编号'),
        disabled: selected.size === 0,
        onClick: copySelectedIds,
      },
    ];
    if (enablePrintAction) {
      menu.push({
        key: 'batch-print-boxmark',
        label: printing ? fixedUi('打印中…') : fixedUi('批量打印箱唛'),
        disabled: !shopId || printing || selected.size === 0,
        onClick: printSelected,
      });
    }
    menu.push({
      key: 'clear-selection',
      label: '清除选中',
      disabled: selected.size === 0,
      onClick: clearSelection,
    });
    return menu;
  }, [selected.size, enablePrintAction, printing, shopId, tableMeta, moduleKey]);

  const emptyTitle = useMemo(() => {
    if (!shopId) return '请先选择店铺';
    if (loading) return '加载中…';
    if (neverSynced && rows.length === 0) return `尚未同步${title}`;
    if (rows.length === 0) return `暂无${title}`;
    return '暂无数据';
  }, [shopId, loading, neverSynced, rows.length, title]);

  const emptyHint = useMemo(() => {
    if (!shopId) return '';
    if (neverSynced && rows.length === 0 && !loading) {
      return `${fixedUi('点击「拉取 TEMU 后台数据」')} · ${fixedUi(title)}`;
    }
    if (rows.length === 0 && !loading && appliedKeyword) {
      return fixedUi('请调整筛选条件或清空后重试。');
    }
    return '';
  }, [shopId, neverSynced, rows.length, loading, appliedKeyword, title]);

  const colSpan = tableMeta.columns.length + 1 + (enablePrintAction ? 1 : 0);

  const extraActions = [];
  if (enablePrintAction) {
    extraActions.push({
      key: 'print-boxmark',
      label: printing ? fixedUi('打印中…') : fixedUi('打印箱唛'),
      tone: 'primary',
      disabled: !shopId || printing || selected.size === 0,
      onClick: printSelected,
    });
  }

  return (
    <>
      <TemuSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selected.size}
            busy={loading || syncing || printing || exporting}
            syncKinds={syncKinds}
            quotaKind={syncKind}
            onSync={onSync}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={SYNC_HINTS[moduleKey] || `从 TEMU 后台拉取${title}，更新到 IPE 本地。`}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selected.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
            batchOpsMenu={batchOpsMenu}
            extraActions={extraActions}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--w300">
              <span className="tpo-field__label">{fixedUi(tableMeta.keywordLabel)}</span>
              <input
                className="tpo-input"
                type="search"
                value={filterDraft.keyword}
                disabled={!shopId}
                placeholder={fixedUi(tableMeta.keywordPlaceholder)}
                onChange={(e) => setFilterDraft({ keyword: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!shopId}
              onClick={onReset}
            >
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="tpo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={`正在查询${title}…`}
      >
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-ops-table">
            <thead>
              <tr>
                <th scope="col" className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                    aria-label={fixedUi("全选")}
                    disabled={!rows.length}
                  />
                </th>
                {tableMeta.columns.map((col) => (
                  <th key={col.key} scope="col">{fixedUi(col.label)}</th>
                ))}
                {enablePrintAction ? <th scope="col">{fixedUi("操作")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyTitle}</p>
                      {emptyHint ? <p className="tpo-certs-empty__hint">{emptyHint}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index);
                  const sn = row.deliveryOrderSn || row.taskNo;
                  return (
                    <tr key={key}>
                      <td className="tpo-ops-td tpo-ops-td--check temu-product-cell--spu-check">
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggleRow(key)}
                          aria-label={`选择 ${sn || key}`}
                        />
                      </td>
                      {tableMeta.columns.map((col) => (
                        <td key={col.key}>{col.pick(row)}</td>
                      ))}
                      {enablePrintAction ? (
                        <td>
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            disabled={!sn || printing}
                            onClick={async () => {
                              if (!sn || !shopId) return;
                              setPrinting(true);
                              try {
                                const res = await temuPlatformAPI.printLabels(shopId, {
                                  kind: 'boxmark',
                                  deliveryOrderSnList: [String(sn)],
                                  return_data_key: true,
                                });
                                const url = res?.printUrl || res?.url;
                                if (url && typeof window !== 'undefined') {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                  showToast(fixedUi("已打开打印页"), 'success');
                                } else {
                                  showToast(res?.msg || '打印已提交', 'info');
                                }
                              } catch (e) {
                                showToast(e?.message || '打印失败', 'error');
                              } finally {
                                setPrinting(false);
                              }
                            }}
                          >
                            打印
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ProductListCard>
    </>
  );
}
